import {
    FormControl,
    ValidationErrors,
    Validators,
} from '@angular/forms';
import UiValidators from '@validators';

export interface InputDefinition {
    type: InputType;
    validator: (fc: FormControl) => ValidationErrors | null;
    restriction?: RegExp;
}

export enum InputType {
    Number,
    Boolean,
    String,
    Complex,
}

namespace Definitions {
    export const decimalNumber = (min: number, max: number): InputDefinition => ({
        type: InputType.Number,
        validator: (c: FormControl) => {
            if (
                c.value == null ||
                c.value === ''
            ) { return null; }

            const value = Number(c.value);
            return !Number.isNaN(value) ?
                UiValidators.range(min, max)(c) : {
                    decimalNumber: true,
                };
        },
        restriction: /^[-0-9.]+$/,
    });

    export const bool = (): InputDefinition => ({
        type: InputType.Boolean,
        validator: () => null,
    });

    export const wholeNumber = (min: number, max: number): InputDefinition => ({
        type: InputType.Number,
        validator: (c: FormControl) => {
            if (
                c.value == null ||
                c.value === ''
            ) { return null; }

            const value = Number(c.value);
            return !Number.isNaN(value) ?
                UiValidators.range(min, max)(c) : {
                    wholeNumber: true,
                };
        },
        restriction: /^[-0-9]+$/,
    });

    export const string = (max: number): InputDefinition => ({
        type: InputType.String,
        validator: (c: FormControl) => {
            return Validators.maxLength(max)(c);
        },
    });
}

export namespace ClrType {
    const regex = {
        namespace: '^([\\w.]+)',
        name: '(\\.)(\\w+)',
        genericSeparator: '(`)',
        genericArgumentCount: '(\\d)',
        genericArgumentTypes: '(\\[)(.+)(\\])',
        arraySuffix: /(\[\])*$/,
        extendedInfo: /, [0-9a-zA-Z\.]+, Version=[0-9a-z\.]+, Culture=[0-9a-zA-Z\.]+, PublicKeyToken=[a-f0-9]+/g,
        clrType: () => {
            const nonGenericType = `${regex.namespace}${regex.name}`;

            const genericType = `${nonGenericType}${regex.genericSeparator}${
                regex.genericArgumentCount}${regex.genericArgumentTypes}`;

            return new RegExp(`${genericType}|${nonGenericType}`);
        },
    };

    export enum PrimitiveType {
        bool = 'System.Boolean',
        byte = 'System.Byte',
        sbyte = 'System.SByte',
        char = 'System.Char',
        double = 'System.Double',
        float = 'System.Single',
        int = 'System.Int32',
        uint = 'System.UInt32',
        long = 'System.Int64',
        ulong = 'System.UInt64',
        short = 'System.Int16',
        ushort = 'System.UInt16',
        string = 'System.String',
    }

    export enum DateType {
        dateTime = 'System.DateTime',
        dateTimeOffset = 'System.DateTimeOffset',
    }

    export enum ComplexType {
        object = 'System.Object',
    }


    export interface IClrType {
        display: string;
        type: string;
        name: string;
        namespace: string;
        fullName: string;
        isGeneric: boolean;
        arguments: {
            count: number,
            list?: IClrType[],
        };
    }

    const parseGenericTypeList = (types: any, cb: (_: string) => void) => {
        const [, genericTypeRaw] = types;
        const subGroupDelimiters: any[] = [];
        let level = 0;

        // determine the first level type groups
        genericTypeRaw.split('')
            .forEach((ch: string, idx: number) => {
                if (ch === '[') { level += 1; }
                if (ch === ']') { level -= 1; }
                if (
                    level === 0 &&
                    ch === ','
                ) {
                    subGroupDelimiters.push(idx);
                }
            });

        // add the last subgroup index
        subGroupDelimiters.push(genericTypeRaw.length);

        for (let i = 0; i < subGroupDelimiters.length; i += 1) {
            const previousIndex = i ? subGroupDelimiters[i - 1] : -1;
            // display start and end indices
            // in order to remove extra commas and extra parenthesis
            const group = genericTypeRaw.substring(previousIndex + 2, subGroupDelimiters[i] - 1);
            cb(group);
        }
    };

    export const parseType = (clrType: string): IClrType => {
        let cleanType = clrType.replace(regex.extendedInfo, '');
        let clrNamespace, separator, name, countSeparator, count, types, arraySuffix;

        try {
            // extract array multi-dimensional suffix
            [arraySuffix] = cleanType.match(regex.arraySuffix)!;
            // clean array suffix from parsed type
            cleanType = cleanType.replace(regex.arraySuffix, '');
            [, clrNamespace, separator, name, countSeparator, count, ...types] = regex.clrType()!
                .exec(cleanType)!
                .filter(m => !!m);
        } catch {
            // use 'System.Object' as a fallback if something goes wrong (unknown library / version)
            [, clrNamespace, separator, name, countSeparator, count, ...types] = regex.clrType()!
                .exec(ClrType.ComplexType.object)!
                .filter(m => !!m);
        }

        const isGeneric = countSeparator === '`';

        const type: IClrType = {
            type: clrType,
            namespace: clrNamespace,
            fullName: `${clrNamespace}${separator}${name}`,
            display: name + arraySuffix,
            name: name,
            isGeneric,
            arguments: {
                count: isGeneric ? Number(count) : 0,
                list: [],
            },
        };

        if (type.isGeneric) {
            parseGenericTypeList(
                types,
                (group) => type.arguments.list!.push(parseType(group)),
            );

            type.display = `${type.name}<${
                type.arguments.list!
                    .map(t => t.display)
                    .join(', ')
                }>${arraySuffix}`;
        }

        return type;
    };

    export const inputConfiguration = (clrType: string) => {
        const [type] = (clrType || '')
            .split(',');
        switch (type) {
            case ClrType.PrimitiveType.bool:
                return Definitions.bool();

            case ClrType.PrimitiveType.byte:
                return Definitions.wholeNumber(0, 255);
            case ClrType.PrimitiveType.sbyte:
                return Definitions.wholeNumber(-128, 127);
            case ClrType.PrimitiveType.int:
                return Definitions.wholeNumber(-2147483648, 2147483647);
            case ClrType.PrimitiveType.uint:
                return Definitions.wholeNumber(0, 4294967295);
            case ClrType.PrimitiveType.long:
                return Definitions.wholeNumber(Number.MIN_SAFE_INTEGER, Number.MAX_SAFE_INTEGER);
            case ClrType.PrimitiveType.ulong:
                return Definitions.wholeNumber(0, Number.MAX_SAFE_INTEGER);
            case ClrType.PrimitiveType.short:
                return Definitions.wholeNumber(-32768, 32767);
            case ClrType.PrimitiveType.ushort:
                return Definitions.wholeNumber(0, 65535);

            case ClrType.PrimitiveType.char:
                return Definitions.string(1);
            case ClrType.PrimitiveType.string:
                return Definitions.string(2000);

            case ClrType.PrimitiveType.double:
            case ClrType.PrimitiveType.float:
                return Definitions.decimalNumber(Number.MIN_SAFE_INTEGER, Number.MAX_SAFE_INTEGER);

            case ClrType.DateType.dateTime:
            case ClrType.DateType.dateTimeOffset:
            default:
                return {
                    type: InputType.Complex,
                    validator: (c: FormControl) => {
                        if (
                            c.value == null ||
                            (typeof c.value === 'string' && !c.value)
                        ) { return null; }

                        try {
                            JSON.parse(c.value);
                            return null;
                        } catch {
                            return {
                                json: true,
                            };
                        }
                    },
                };
        }
    };
}
